import { theme } from '@squire/ui';

const WIDE_SCREEN_MIN_WIDTH = 550;
const MIN_MONTH_GAP = theme.spacing[8];

export interface CalendarLayout {
  monthWidth: number;
  monthGap: number;
  monthsPerPage: number;
  /** Distance between the left edges of two adjacent months. */
  monthStride: number;
  trailingPadding: number;
}

/**
 * Sizes the month list so every page starts on a month boundary. On wide
 * screens months are capped at phone width, so as many as fit sit side by side
 * and stretch the gap to fill the list; when only one fits, the gap pushes the
 * next month fully off the page. Scrolling, arrows and the titles all use
 * `monthStride`, so the visible months always match the title.
 */
export const getCalendarLayout = ({
  listWidth,
  screenWidth,
}: {
  listWidth: number;
  screenWidth: number;
}): CalendarLayout => {
  const maxMonthWidth =
    screenWidth >= WIDE_SCREEN_MIN_WIDTH
      ? Math.min(screenWidth, theme.breakpoints.mobile) - theme.spacing[8]
      : listWidth;
  const monthWidth = Math.max(0, Math.min(listWidth, maxMonthWidth));

  if (monthWidth === 0) {
    return {
      monthWidth: 0,
      monthGap: 0,
      monthsPerPage: 1,
      monthStride: 0,
      trailingPadding: 0,
    };
  }

  const monthsPerPage = Math.max(
    1,
    Math.floor((listWidth + MIN_MONTH_GAP) / (monthWidth + MIN_MONTH_GAP)),
  );

  if (monthsPerPage === 1) {
    const monthGap = listWidth - monthWidth;
    return {
      monthWidth,
      monthGap,
      monthsPerPage,
      monthStride: listWidth,
      trailingPadding: monthGap,
    };
  }

  const monthGap =
    (listWidth - monthsPerPage * monthWidth) / (monthsPerPage - 1);
  return {
    monthWidth,
    monthGap,
    monthsPerPage,
    monthStride: monthWidth + monthGap,
    trailingPadding: 0,
  };
};
